import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";
import { Customer } from "../types/user";

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  },
});

export const customerSlice = createApi({
  reducerPath: "customerSlice",
  baseQuery,
  tagTypes: ["Customers", "Orders"],
  // keep data cached for 5 minutes to avoid refetching when navigating
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: 30, // only refetch if older than 30 seconds
  refetchOnFocus: false, // don't refetch just because window gets focus
  refetchOnReconnect: true, // do refetch when reconnecting
  endpoints: (builder) => ({
    getCustomers: builder.query<Customer[], void>({
      query: () => "/customers",
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: "Customers" as const,
                id: _id,
              })),
              { type: "Customers", id: "LIST" },
            ]
          : [{ type: "Customers", id: "LIST" }],
    }),

    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({
        url: `/customers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Customers", id },
        { type: "Customers", id: "LIST" },
        { type: "Orders", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useDeleteCustomerMutation,
  // export prefetch methods for manual prefetching
  usePrefetch,
} = customerSlice;
